/**
 * AlertsSection Component - Threshold alerts and milestone celebrations
 */
import React, { useState } from 'react';
import type { AlertThreshold, Milestone } from './types';

interface AlertsSectionProps {
  thresholds: AlertThreshold[];
  milestones: Milestone[];
  currentRevenue: number;
  currentShares: number;
  onThresholdsChange: (thresholds: AlertThreshold[]) => void;
}

export const AlertsSection: React.FC<AlertsSectionProps> = ({
  thresholds, milestones, currentRevenue, currentShares, onThresholdsChange
}) => {
  const [showConfig, setShowConfig] = useState(false);
  const [newThreshold, setNewThreshold] = useState<Partial<AlertThreshold>>({ metric: 'revenue', operator: 'below', value: 0, enabled: true });

  // Check which thresholds are triggered
  const triggeredAlerts = thresholds.filter(t => {
    const value = t.metric === 'revenue' ? currentRevenue : currentShares;
    if (t.operator === 'above') return value > t.value;
    return value < t.value;
  });

  // Recently achieved milestones
  const achievedMilestones = milestones.filter(m => m.achieved);
  const upcomingMilestones = milestones.filter(m => !m.achieved);

  const addThreshold = () => {
    if (newThreshold.value && newThreshold.value > 0) {
      const threshold: AlertThreshold = {
        id: `threshold-${Date.now()}`,
        metric: newThreshold.metric || 'revenue',
        operator: newThreshold.operator || 'below',
        value: newThreshold.value,
        enabled: true
      };
      onThresholdsChange([...thresholds, threshold]);
      setNewThreshold({ metric: 'revenue', operator: 'below', value: 0, enabled: true });
    }
  };

  const removeThreshold = (id: string) => {
    onThresholdsChange(thresholds.filter(t => t.id !== id));
  };

  const toggleThreshold = (id: string) => {
    onThresholdsChange(thresholds.map(t => t.id === id ? { ...t, enabled: !t.enabled } : t));
  };

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold flex items-center gap-2">🔔 Alerts & Milestones</h3>
        <button onClick={() => setShowConfig(!showConfig)} className="text-sm text-gray-400 hover:text-white">
          {showConfig ? 'Hide' : 'Configure'}
        </button>
      </div>

      {/* Triggered Alerts */}
      {triggeredAlerts.length > 0 && (
        <div className="mb-4 space-y-2">
          {triggeredAlerts.map(alert => (
            <div key={alert.id} className="flex items-center gap-2 p-2 bg-red-900/30 border border-red-800/50 rounded-lg">
              <span className="text-red-400">⚠️</span>
              <p className="text-red-300 text-sm flex-1">
                {alert.metric.charAt(0).toUpperCase() + alert.metric.slice(1)} is {alert.operator} ${alert.value.toLocaleString()}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Milestone Celebrations */}
      {achievedMilestones.length > 0 && (
        <div className="mb-4 space-y-2">
          {achievedMilestones.slice(-3).map(milestone => (
            <div key={milestone.id} className="flex items-center gap-2 p-2 bg-green-900/30 border border-green-800/50 rounded-lg">
              <span className="text-2xl">🎉</span>
              <div>
                <p className="text-green-300 text-sm font-medium">{milestone.name}</p>
                <p className="text-green-500 text-sm">{milestone.achievedDate && `Achieved ${new Date(milestone.achievedDate).toLocaleDateString()}`}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upcoming Milestones Progress */}
      {upcomingMilestones.length > 0 && (
        <div className="mb-4">
          <p className="text-white text-sm mb-2">Next Milestones</p>
          {upcomingMilestones.slice(0, 2).map(milestone => {
            const progress = Math.min(100, (currentRevenue / milestone.target) * 100);
            return (
              <div key={milestone.id} className="mb-2">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-white">{milestone.name}</span>
                  <span className="text-gray-500">${currentRevenue.toLocaleString()} / ${milestone.target.toLocaleString()}</span>
                </div>
                <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-500 transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Configuration Panel */}
      {showConfig && (
        <div className="border-t border-gray-700 pt-4 mt-4">
          <p className="text-white text-sm mb-3">Alert Thresholds</p>
          
          {/* Existing thresholds */}
          <div className="space-y-2 mb-4">
            {thresholds.map(t => (
              <div key={t.id} className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={t.enabled} onChange={() => toggleThreshold(t.id)} className="rounded" />
                <span className={t.enabled ? 'text-white' : 'text-gray-500'}>
                  {t.metric} {t.operator} ${t.value.toLocaleString()}
                </span>
                <button onClick={() => removeThreshold(t.id)} className="text-red-400 hover:text-red-300 ml-auto">✕</button>
              </div>
            ))}
          </div>

          {/* Add new threshold */}
          <div className="flex flex-wrap gap-2 items-end">
            <select value={newThreshold.metric} onChange={e => setNewThreshold({ ...newThreshold, metric: e.target.value as AlertThreshold['metric'] })}
              className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm">
              <option value="revenue">Revenue</option>
              <option value="shares">Shares</option>
            </select>
            <select value={newThreshold.operator} onChange={e => setNewThreshold({ ...newThreshold, operator: e.target.value as AlertThreshold['operator'] })}
              className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm">
              <option value="below">Below</option>
              <option value="above">Above</option>
            </select>
            <input type="number" placeholder="Value" value={newThreshold.value || ''} onChange={e => setNewThreshold({ ...newThreshold, value: Number(e.target.value) })}
              className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm w-24" />
            <button onClick={addThreshold} className="bg-blue-600 hover:bg-blue-500 text-white px-3 py-1 rounded text-sm">Add</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlertsSection;

